/**
 * Accepted image MIME types for the reference image upload.
 */
export const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';

/**
 * Maximum allowed reference image size, in megabytes.
 */
export const MAX_FILE_SIZE_MB = 10;

/**
 * Maximum allowed reference image size, in bytes.
 */
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Upload states for the reference image upload flow.
 */
export const UPLOAD_STATES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  COMPLETE: 'complete',
  ERROR: 'error'
};

/**
 * Reads a file and converts it to a base64 string (without the data URI prefix).
 *
 * @param {File} file - The image file to convert
 * @returns {Promise<{base64: string, mimeType: string}>}
 */
export const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:<mime>;base64,<data>" — strip the prefix
      const dataUrl = reader.result;
      const base64 = dataUrl.split(',')[1];
      resolve({ base64, mimeType: file.type });
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Analytics labels for each resolved inputType. Anything unmapped is plain text.
 */
export const INPUT_TYPE_ANALYTICS_LABELS = {
  VOICE_TO_TEXT: 'speech to text',
  IMAGE: 'image',
  IMAGE_AND_TEXT: 'image and text',
  IMAGE_AND_VOICE: 'image and speech to text'
};

/**
 * Resolve the inputType sent to the list agent.
 *
 * Image modes take precedence over text/voice. An image paired with text
 * distinguishes dictated (IMAGE_AND_VOICE) from typed (IMAGE_AND_TEXT) so the
 * list agent gets the right instructions. Voice-to-text is the only mode that
 * survives a no-image generate — a stale image-based value from a prior
 * generate must not leak in.
 */
export const resolveInputType = ({ inputType, hasImage, hasPrompt }) => {
  if (!hasImage) return inputType === 'VOICE_TO_TEXT' ? 'VOICE_TO_TEXT' : 'TEXT';
  if (!hasPrompt) return 'IMAGE';
  return inputType === 'VOICE_TO_TEXT' ? 'IMAGE_AND_VOICE' : 'IMAGE_AND_TEXT';
};

/**
 * Build the templateData array for an `interactWithAgent` generate request.
 *
 * Pass `referenceImage: null` to omit the image keys — the auto-trigger path
 * has no image to send.
 */
export const buildTemplateData = ({
  existingMaterials,
  name,
  questionerEnabled,
  referenceImage
}) => [
  { key: 'materialsGroup', value: JSON.stringify(existingMaterials || []) },
  { key: 'title', value: name },
  ...(questionerEnabled ? [{ key: 'bathroomQuestionsEnabled', value: 'true' }] : []),
  ...(referenceImage ? [
    { key: 'referenceImageBase64', value: referenceImage.base64 },
    { key: 'referenceImageMimeType', value: referenceImage.mimeType }
  ] : [])
];
